import React, { useState } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { EyeOff, Check, X } from 'lucide-react';
import { ignoreBrokerInvoice, unignoreBrokerInvoice } from '../services/brokerInvoiceLookup';

/**
 * ActualCost2Ignore — CHA-379. Shared ignore/un-ignore for commercial invoices that will never get
 * a broker job# (very small, prepaid, shipped via parcel — e.g. AC001-4, 40 units via DHL). Used by
 * BOTH the dashboard's missing panel and the Links tab, so the write path exists exactly once.
 *
 * The mark lives on the invoice's `lookup_broker_invoice` doc as `status: 'ignored'` +
 * `ignore_reason` (+ optional `ignore_note`) — NEVER a job_number, so an ignore can't mint a fake
 * link (loadLookup only admits valid-shape jobs, and a real link always wins over the mark).
 * Reason choices are operator-editable in AC2 → Settings (`ignoreReasons` on actual_cost_2_config).
 */

const CONFIG_COLLECTION = 'actual_cost_2_config';

export const DEFAULT_IGNORE_REASONS = ['small', 'prepaid', 'parcel'];

// Reason list from AC2 Settings; falls back to the seeded defaults when unset/empty.
export async function fetchIgnoreReasons(tenantId) {
  if (!tenantId) return DEFAULT_IGNORE_REASONS;
  try {
    const snap = await getDoc(doc(getFirestore(), CONFIG_COLLECTION, tenantId));
    const list = snap.exists() ? snap.data()?.ignoreReasons : null;
    const cleaned = Array.isArray(list) ? list.map((s) => String(s).trim()).filter(Boolean) : [];
    return cleaned.length ? cleaned : DEFAULT_IGNORE_REASONS;
  } catch {
    return DEFAULT_IGNORE_REASONS;
  }
}

export async function ignoreInvoice(inv, reason, note, tenantId) {
  await ignoreBrokerInvoice(tenantId, inv, reason, note);
}

// Restores the invoice to the missing worklist. A pure ignore doc is DELETED (which also re-opens
// container-bridge seeding — the bridge skips any existing doc); a doc the ignore was merged onto
// (e.g. a CHA-355 garbage-job doc the operator can still fix) keeps its pre-existing fields and
// only loses the ignore mark (self-review P2).
export async function unignoreInvoice(inv, tenantId) {
  await unignoreBrokerInvoice(tenantId, inv);
}

/**
 * Inline ignore control: an "ignore" trigger that expands to reason-select + optional note +
 * confirm/cancel. On success calls onDone(inv, reason) — the host list handles its own refresh.
 */
export function IgnoreControl({ inv, reasons, tenantId, onDone, onError }) {
  const list = (reasons && reasons.length ? reasons : DEFAULT_IGNORE_REASONS);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(list[0]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await ignoreInvoice(inv, reason, note, tenantId);
      setOpen(false); setNote('');
      onDone?.(inv, reason);
    } catch (e) {
      onError?.(`Ignore failed for ${inv}: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => { setReason(list[0]); setOpen(true); }}
        title="Ignore this invoice — small / prepaid / parcel shipments that will never get a broker job#"
        className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 text-xs">
        <EyeOff className="h-3.5 w-3.5" /> ignore
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <select value={reason} onChange={(e) => setReason(e.target.value)}
        className="border border-slate-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500">
        {list.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <input value={note} onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
        placeholder="note (optional)"
        className="w-28 border border-slate-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500" />
      <button onClick={confirm} disabled={busy} title="Confirm ignore" className="text-teal-700 hover:text-teal-800 disabled:opacity-40"><Check className="h-4 w-4" /></button>
      <button onClick={() => { setOpen(false); setNote(''); }} title="Cancel" className="text-slate-500 hover:text-slate-700"><X className="h-4 w-4" /></button>
    </span>
  );
}
